import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import ReceiptService from '../services/ReceiptService';
import Loader from './Loader';

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? String(value)
    : new Intl.DateTimeFormat('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      }).format(date);
};

function ReceiptVerification() {
  const { receiptNumber = '' } = useParams();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const loadVerification = async () => {
      setLoading(true);
      try {
        const data = await ReceiptService.verifyReceipt(receiptNumber);
        if (isMounted) {
          setResult(data);
        }
      } catch (error) {
        if (isMounted) {
          setResult({
            exists: false,
            isValid: false,
            receiptNumber,
            message: error?.response?.data || error?.message || 'Verification failed.'
          });
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadVerification();
    return () => {
      isMounted = false;
    };
  }, [receiptNumber]);

  if (loading) {
    return <Loader overlay={true} text="Verifying receipt..." size="large" />;
  }

  const isValid = Boolean(result?.exists && result?.isValid);
  const accentColor = isValid ? '#14532d' : '#991b1b';
  const accentSoft = isValid ? '#e9f5ee' : '#fdecec';
  const statusText = isValid ? 'Verified' : 'Verification Failed';

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #e9edf5 0%, #f8fafc 100%)',
        padding: '32px 16px'
      }}
    >
      <div className="container">
        <div className="mx-auto" style={{ maxWidth: '980px' }}>
          <div
            style={{
              background: '#ffffff',
              border: '1px solid rgba(20, 32, 51, 0.08)',
              boxShadow: '0 18px 48px rgba(15, 23, 42, 0.12)'
            }}
          >
            <div className="p-4 p-md-5">
              <div
                className="d-grid gap-4"
                style={{
                  gridTemplateColumns: 'minmax(0, 1.4fr) minmax(280px, 1fr)',
                  borderBottom: '2px solid #d7dce5',
                  paddingBottom: '24px'
                }}
              >
                <div className="d-flex flex-column gap-2">
                  <div
                    style={{
                      display: 'inline-flex',
                      width: 'fit-content',
                      padding: '6px 12px',
                      borderRadius: '999px',
                      background: accentSoft,
                      color: accentColor,
                      fontSize: '12px',
                      fontWeight: 700,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase'
                    }}
                  >
                    Secure Receipt Verification
                  </div>
                  <h1 className="mb-0" style={{ fontSize: '30px', lineHeight: 1.15 }}>
                    {isValid ? 'Receipt Verified Successfully' : 'Receipt Could Not Be Verified'}
                  </h1>
                  <p className="mb-0 text-muted" style={{ fontSize: '14px', lineHeight: 1.6 }}>
                    This verification page uses the same secure receipt system and checks whether the
                    receipt number and signature fingerprint belong to Saral Asset Manager.
                  </p>
                </div>

                <div
                  style={{
                    background: 'linear-gradient(180deg, #f5f7fb, #ffffff)',
                    border: '1px solid #d7dce5',
                    borderRadius: '18px',
                    padding: '18px 20px'
                  }}
                >
                  {[
                    ['Receipt No.', result?.receiptNumber || receiptNumber],
                    ['Status', statusText],
                    ['Generated At', formatDateTime(result?.generatedAt)]
                  ].map(([label, value]) => (
                    <div
                      key={label}
                      className="d-flex justify-content-between gap-3 py-2"
                      style={{ borderBottom: '1px dashed rgba(90, 100, 120, 0.18)' }}
                    >
                      <div className="text-muted" style={{ fontSize: '13px' }}>{label}</div>
                      <div style={{ fontSize: '13px', fontWeight: 600, textAlign: 'right' }}>{value || '-'}</div>
                    </div>
                  ))}
                  <div
                    style={{
                      display: 'inline-flex',
                      marginTop: '12px',
                      padding: '7px 12px',
                      borderRadius: '999px',
                      background: accentColor,
                      color: '#fff',
                      fontSize: '12px',
                      fontWeight: 700,
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase'
                    }}
                  >
                    {statusText}
                  </div>
                </div>
              </div>

              <div className="mt-4">
                <div
                  style={{
                    border: `1px solid ${isValid ? '#c7ead3' : '#f1c1c1'}`,
                    background: isValid ? '#f3fbf6' : '#fff5f5',
                    color: isValid ? '#14532d' : '#991b1b',
                    borderRadius: '16px',
                    padding: '16px 18px',
                    fontSize: '15px',
                    lineHeight: 1.6
                  }}
                >
                  {result?.message}
                </div>
              </div>

              <div className="row g-4 mt-1">
                <div className="col-lg-6">
                  <div style={{ color: accentColor, fontSize: '13px', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '12px' }}>
                    Receipt Details
                  </div>
                  <table className="table mb-0" style={{ border: '1px solid #d7dce5' }}>
                    <tbody>
                      {[
                        ['Receipt Number', result?.receiptNumber || receiptNumber],
                        ['Receipt Type', result?.receiptType || '-'],
                        ['Generated By', result?.generatedBy || '-'],
                        ['Generated At', formatDateTime(result?.generatedAt)]
                      ].map(([label, value]) => (
                        <tr key={label}>
                          <td style={{ width: '42%', background: '#f5f7fb', color: '#5a6478', fontWeight: 600 }}>{label}</td>
                          <td>{value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="col-lg-6">
                  <div style={{ color: accentColor, fontSize: '13px', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '12px' }}>
                    Verification Details
                  </div>
                  <table className="table mb-0" style={{ border: '1px solid #d7dce5' }}>
                    <tbody>
                      {[
                        ['Signature Fingerprint', result?.signaturePreview || '-'],
                        ['Source Entity', result?.sourceEntityType || '-'],
                        ['Source ID', result?.sourceEntityId || '-'],
                        ['Issuer', 'Saral Asset Manager']
                      ].map(([label, value]) => (
                        <tr key={label}>
                          <td style={{ width: '42%', background: '#f5f7fb', color: '#5a6478', fontWeight: 600 }}>{label}</td>
                          <td>{value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="mt-4 pt-3" style={{ borderTop: '1px solid #d7dce5' }}>
                <Link
                  to="/"
                  className="btn"
                  style={{
                    borderRadius: '999px',
                    padding: '10px 18px',
                    border: `1px solid ${accentColor}`,
                    color: accentColor,
                    fontWeight: 600
                  }}
                >
                  Open Saral Asset Manager
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ReceiptVerification;
